import { Partner } from '@/types/partner'
import { Dimension } from '@/types/config'
import makePartnerSource from './sources/partner'
import referrerSource from './sources/referrer'
import segmentsSource from './sources/segments'
import pageviewsSource from './sources/pageviews'
import utmSource from './sources/utm'
import querySource from './sources/query'
import jsdataSource from './sources/jsdata'
import wordpressSource from './sources/wordpress'
import deviceSource from './sources/device'
import dateSource from './sources/date'
import scoreSource from './sources/score'
import rightmessageSource from './sources/rightmessage'
import locationSource from './sources/location'

/**
 * Represents a source for logic definition with a label and options.
 */
type LogicDefinitionSource = {
  label: string
  options: Array<{
    label: string
    value: string
  }>
}

const sources: Record<
  string,
  { label: string; source: (context?: any) => any }
> = {
  referrer: { label: 'Referred By', source: referrerSource },
  pageviews: { label: 'On-Site Behavior', source: pageviewsSource },
  segments: { label: 'Segments / Questions', source: segmentsSource },
  utm: { label: 'UTM Parameters', source: utmSource },
  query: { label: 'Query Parameters', source: querySource },
  rightmessage: { label: 'RightMessage', source: rightmessageSource },
  location: { label: 'Location', source: locationSource },
  date: { label: 'Date', source: dateSource },
  scores: { label: 'Scoring & Data', source: scoreSource },
  device: { label: 'Device', source: deviceSource },
  jsdata: { label: 'Browser Data', source: jsdataSource },
  wordpress: { label: 'WordPress', source: wordpressSource }
}

const makeSourceFields = ({
  partner,
  context
}: {
  partner: Partner
  context: { ignoreSegmentGroups: Dimension[] }
}) => {
  const partnerLabel = partner?.label || 'Email List'
  const partnerName = partner?.name || 'espplaceholder'

  return [
    ...[
      {
        group: partnerLabel,
        name: partnerName,
        options: makePartnerSource(partner).types.map((option) => ({
          ...option,
          value: `${partnerName}*${option.value}`
        }))
      }
    ],
    ...Object.keys(sources).map((key) => ({
      group: sources[key].label,
      name: key,
      options: sources[key].source(context).types.map((option: any) => {
        return { ...option, value: `${key}*${option.value}` }
      })
    }))
  ].filter((f) => {
    const route = useRoute()
    const segmentGroupStore = useSegmentGroupStore()
    const activeSegmentGroups = segmentGroupStore.segmentGroups

    if (f.name === 'scores' && !route?.params.offerFunnelId) return false

    if (f.name === 'segments') {
      // Get the ignored segment groups from context
      const ignoredIds = context?.ignoreSegmentGroups?.map(sg => sg.id) || []

      // Check if there are any segment groups that are not ignored
      const hasEligibleSegmentGroups = activeSegmentGroups.some(
        sg => !ignoredIds.includes(sg.id)
      )

      return hasEligibleSegmentGroups
    }

    return true
  })
}

const makeSchema = ({ partner }: { partner: Partner }) => {
  return [
    ...(partner ? makePartnerSource(partner).inputs : []),
    ...Object.keys(sources).flatMap((key) => sources[key].source({}).inputs)
  ]
}

export { makeSchema, makeSourceFields }
